import { MEDIA_TRANSITIONS } from './compareMedia'

// Constants.
import { OPERATIONS } from '../../call/interfaceNew/constants'

/**
 * Based on a MediaDiff object, determine what the call operation was that
 *    caused the SDP/media change.
 * @method getOperation
 * @param  {MediaDiff} mediaDiff
 * @return {string}
 */
export default function getOperation (mediaDiff) {
  // Naively just check one operation after the other.
  // TODO: Make this more efficient?
  // TODO: These MoH checks should take into account current call state.
  //    Start/Stop MoH should only be possible in specific states.
  if (wasHold(mediaDiff) || was3xHold(mediaDiff)) {
    return OPERATIONS.HOLD
  } else if (wasUnhold(mediaDiff) || was3xUnhold(mediaDiff)) {
    return OPERATIONS.UNHOLD
  } else if (wasStartMoh(mediaDiff)) {
    // Check 'start MoH' before 'add media' because 'start MoH' is a special
    //    case of 'add media'.
    return OPERATIONS.START_MOH
  } else if (wasAddMedia(mediaDiff)) {
    return OPERATIONS.ADD_MEDIA
  } else if (wasStopMoh(mediaDiff)) {
    // Check 'stop MoH' before 'remove media' because 'stop MoH' is a special
    //    case of 'remove media'.
    return OPERATIONS.STOP_MOH
  } else if (wasRemoveMedia(mediaDiff)) {
    return OPERATIONS.REMOVE_MEDIA
  } else if (wasNoChange(mediaDiff)) {
    return 'NO_CHANGE'
  } else {
    return 'UNKNOWN'
  }
}

/**
 * A "hold" operation is when all media lines change to be "inactive".
 * Some media may have already been inactive. "Hold" is specifically when
 *    all active media becomes inactive, hence there is no longer any media
 *    flowing.
 *
 * This differs from a "stop MoH" operation by requiring at least one media to
 *    be "flowing" (both sending and receiving) before the operation. For
 *    "stop MoH", the media would only be sending (and not receiving) before.
 *
 * In terms of SDP changes, it is defined as:
 *    1) At least one media was 'flowing' before the SDP change.
 *    2) No media is 'flowing' after the SDP change.
 *    3) No active media was added.
 *    4) No media was removed.
 *    5) All media that changed was stopped.
 *        ie. is being "held".
 *    6) All media that is unchanged is not sending/receiving.
 *        ie. was already "held".
 * @method wasHold
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasHold (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  /*
   * 1 & 2) Some media was flowing before the change, and no media is flowing
   *    after the change. This ensures "hold" is distinct from "stop MoH"
   *    scenarios.
   */
  const stoppedFlowing = hadMediaFlowing(mediaDiff) && !hasMediaFlowing(mediaDiff)

  /*
   * 3) No active media was added.
   * For any added media we need to check if it's inactive to determine if this was a hold
   */
  const noActiveMediaAdded = !added.some(media => media.willSend || media.willReceive)

  /*
   * 4) No media was removed
   */
  const noMediaRemoved = removed.length === 0

  /*
   * 5a) Some media did change.
   */
  const didChange = changed.length > 0

  /*
   * 5b) For all media that changed,
   *    all changes were either `stop` or `remain stopped`.
   */
  const allStopped = changed.every(({ media, changes }) => {
    // This media is not sending.
    const notSending =
      changes.sending === MEDIA_TRANSITIONS.STOP || (changes.sending === MEDIA_TRANSITIONS.SAME && !media.willSend)
    // This media is not receiving.
    const notReceiving =
      changes.receiving === MEDIA_TRANSITIONS.STOP ||
      (changes.receiving === MEDIA_TRANSITIONS.SAME && !media.willReceive)

    return notSending && notReceiving
  })

  /*
   * 6) For all media that was not changed,
   *    no media is being sent/received.
   */
  const stillStopped = unchanged.every(media => {
    return !media.willSend && !media.willReceive
  })

  return stoppedFlowing && noActiveMediaAdded && noMediaRemoved && didChange && allStopped && stillStopped
}

/**
 * An "unhold" operation is when all media lines were previously inactive and
 *    some change to be active.
 * Not all media needs to change to be active, only some.
 *
 * This differs from a "start MoH" operation by requiring some media to be
 *    "flowing" (both sending and receiving) after the operation. For
 *    "start MoH", the media would only be sending (and not receiving) after.
 *
 * In terms of SDP changes, it is defined as:
 *    1) No media was flowing beforehand.
 *    2) At least one media is flowing afterwards.
 *    3) No media was added.
 *    4) No media was removed.
 *    5) All media that changed was started.
 *        ie. is being "unheld"
 *    6) All media that is unchanged is not sending/receiving.
 *        ie. was (and still is) "held"
 * @method wasUnhold
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasUnhold (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  /*
   * 1 & 2) No media was flowing before the change, and some media is flowing
   *    after the change. This ensures "unhold" is distinct from "start MoH"
   *    scenarios.
   */
  const startedFlowing = !hadMediaFlowing(mediaDiff) && hasMediaFlowing(mediaDiff)

  /*
   * 3 & 4) No media was added or removed.
   */
  const sameMedia = added.length === 0 && removed.length === 0

  /*
   * 6) For all media that was not changed,
   *    no media is being sent/received.
   */
  const noUnchangedSend = unchanged.every(media => {
    return !media.willSend && !media.willReceive
  })

  /*
   * 5a) Some media did change.
   */
  const didChange = changed.length > 0

  /*
   * 5b) For all media that was changed,
   *    all changes were either `start` or `remain stopped`.
   */
  const onlyStartChanges = changed.every(({ media, changes }) => {
    const onlyStartSending =
      changes.sending === MEDIA_TRANSITIONS.START || (changes.sending === MEDIA_TRANSITIONS.SAME && !media.willSend)

    const onlyStartReceiving =
      changes.receiving === MEDIA_TRANSITIONS.START ||
      (changes.receiving === MEDIA_TRANSITIONS.SAME && !media.willReceive)

    return onlyStartSending && onlyStartReceiving
  })

  return startedFlowing && sameMedia && noUnchangedSend && didChange && onlyStartChanges
}

/**
 * A "3.X hold" operation is when a hold operation changes media to `sendonly`
 *    instead of `inactive`. This is what the 3.X SDK and Mobile SDK does. This
 *    is for interop with remote endpoints other than the 4.X SDK.
 *
 * In terms of SDP changes, it is defined as:
 *    1) At least one media was 'flowing' before the SDP change.
 *    2) All changed audio media is now 'sendonly'.
 *        ie. is being "v3.X held"
 *       All changed video media is not receiving.
 *    3) No active media was added.
 *    4) No media was removed.
 *    5) All media that is unchanged is not sending/receiving.
 *        ie. was already "held".
 *
 * @method was3xUnhold
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function was3xHold (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  /*
   * 1) Handles when some media was flowing before the change.
   *  or a special case where media was flowing but assumes media wasn't flowing.
   * 2) All changed media is now "sendonly".
   *    All changed video media is not receiving.
   */
  const wasFlowing = hadMediaFlowing(mediaDiff)
  const allSendOnly = changed.every(({ media, changes }) => {
    /**
     * Possible scenarios:
     *
     *  1. Audio-only 3.X Hold:
     *    - A sendrecv --> A sendonly
     *
     *  2. Two way Audio-Video (eg. KAA-2639):
     *    - A/V sendrecv/sendrecv --> A/V sendonly/inactive
     *  3. Two way Audio-Video (eg. KAA-2463):
     *    - A/V sendrecv/sendrecv --> A/V sendonly/sendonly
     *
     *  4. Two way Audio, One way receive Video:
     *    - A/V sendrecv/recvonly --> A/V sendonly/inactive
     *  5. Two way Audio, One way receive Video:
     *    - A/V sendrecv/recvonly --> A/V sendonly/sendonly
     *
     *  6. Two way Audio, One way send Video:
     *    - A/V sendrecv/sendonly --> A/V sendonly/inactive
     *  7. Two way Audio, One way send Video:
     *    - A/V sendrecv/sendonly --> A/V sendonly/sendonly (video checked by 5) below)
     *
     * The summary of these scenarios are:
     *  - Audio always stops receiving.
     *  - Audio always continues sending.
     *  - Video is never receiving afterwards (stop, same).
     *  - Video sending can be anything (same, start, stop).
     */
    if (media.type === 'audio') {
      // Changed audio always stops being received by the remote endpoint.
      // Changed audio always continues being sent by the remote endpoint.
      return changes.receiving === MEDIA_TRANSITIONS.STOP && changes.sending === MEDIA_TRANSITIONS.SAME
    } else {
      return (
        // Changed video is never received afterwards.
        //     Either stopped or stayed "not receiving".
        changes.receiving === MEDIA_TRANSITIONS.STOP ||
        (changes.receiving === MEDIA_TRANSITIONS.SAME && media.willReceive === false)
      )
    }
  })

  /*
   * 3) No active media was added.
   * For any added media we need to check if it's inactive to determine if this was a hold
   */
  const noActiveMediaAdded = !added.some(media => media.willSend || media.willReceive)

  /*
   * 4) No media was removed
   */
  const noMediaRemoved = removed.length === 0

  /*
   * 5) For all media that was not changed,
   *      no media is being sent/received,
   *      or video might still be sendonly.
   */
  const noUnchangedSend = unchanged.every(media => {
    return (
      (!media.willSend && !media.willReceive) ||
      // Edge-case: One-way video was being received, and the hold did not
      //    change the direction but just stopped the video being sent.
      (media.type === 'video' && media.willSend && !media.willReceive)
    )
  })

  return wasFlowing && allSendOnly && noActiveMediaAdded && noMediaRemoved && noUnchangedSend
}

/**
 * A "3.X unhold" operation is when a v3.X SDK performs unhold. This is for
 *    interop.
 *
 * This operation is the same as a "regular" unhold, except that media changes
 *    from sendonly instead of inactive.
 *
 * In terms of SDP changes, it is defined as:
 *    1) Some media is flowing afterwards.
 *    2) All media that changed was started receiving.
 *        ie. is being "3.X unheld"
 *    3) No media was added.
 *    4) No media was removed.
 *    5) All media that is unchanged is not sending/receiving.
 *        ie. was (and still is) "held"
 *
 * @method was3xUnhold
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function was3xUnhold (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  /*
   * 1) Some media was flowing before the change.
   * 2) All changed media is now went from "sendonly" to "sendrecv".
   */
  const isFlowing = hasMediaFlowing(mediaDiff)
  const allSending = changed.every(({ media, changes }) => {
    return (
      (changes.sending === MEDIA_TRANSITIONS.SAME && changes.receiving === MEDIA_TRANSITIONS.START) ||
      /*
       * Special-case: If the media is video, it's possible to go from "inactive" to "sendrecv".
       *    This can happen when the SDK is receiving MoH then is unheld immediately (without
       *    going through a stop MoH operation).
       * This behaviour was seen when the remote endpoint was a SIP device. See KAA-2593.
       */
      (media.type === 'video' &&
        changes.sending === MEDIA_TRANSITIONS.START &&
        changes.receiving === MEDIA_TRANSITIONS.START)
    )
  })

  /*
   * 3) & 4) No media was added or removed.
   */
  const sameMedia = added.length === 0 && removed.length === 0

  /*
   * 5) For all media that was not changed,
   *    no media is being sent/received.
   */
  const noUnchangedSend = unchanged.every(media => {
    return !media.willSend && !media.willReceive
  })

  return isFlowing && allSending && sameMedia && noUnchangedSend
}

/**
 * An "add media" operation is when the latest SDP is sending tracks that the
 *    previous SDP was not sending. This can either be a new, sending media
 *    section or an existing media section that was changed to now send.
 *
 * The "start MoH" scenario is a special-case of "add media". When one audio
 *    track is added/started, with no media flowing beforehand, then that could
 *    either be a "start MoH" or "add media" operation. From a webrtc/SDP
 *    viewpoint, they are the same thing in that scenario, so there is no way
 *    to tell them apart using only a MediaDiff object.
 * If both ends of the Call are SDK clients, then "start MoH" can only be done
 *    when the Call is on hold (by definition), and "add media" can only be
 *    done when the Call is not on hold (requirement set by the API), but we
 *    can't make that assumption.
 *
 * In terms of SDP changes, it is defined as:
 *    1) No media [sections] was removed.
 *    2) Any media added is sending.
 *    3) Any media that changed started sending.
 * @method wasAddMedia
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasAddMedia (mediaDiff) {
  const { added, removed, changed } = mediaDiff

  /*
   * 1) No media sections were removed.
   */
  const wasntRemoved = removed.length === 0

  /*
   * 2a & 3a) Something was added or changed.
   */
  const somethingStarted = added.length > 0 || changed.length > 0

  /*
   * 2b) Any media sections added are sending.
   */
  const addedSending = added.every(media => media.willSend)

  /*
   * 3b) Any media that changed was to start sending.
   */
  const changedSending = changed.every(({ media, changes }) => {
    // Should handle:
    //    reconly  --> sendrecv
    //    inactive --> sendonly
    //    inactive --> sendrecv
    return (
      changes.sending === MEDIA_TRANSITIONS.START &&
      (changes.receiving === MEDIA_TRANSITIONS.SAME || changes.receiving === MEDIA_TRANSITIONS.START)
    )
  })

  /*
   * Unchanged media is not checked for.
   *    By definition, it is the same as before, and could be in any state.
   */

  return wasntRemoved && somethingStarted && addedSending && changedSending
}

/**
 * A "remove media" operation is when some media sections stop sending (with no
 *    other changes).
 *
 * The "stop MoH" scenario is a special-case of "remove media". When one audio
 *    track is removed, leaving no media flowing afterwards, then that could
 *    either be a "stop MoH" or "remove media" operation. From a webrtc/SDP
 *    viewpoint, they are the same thing in that scenario, so there is no way
 *    to tell them apart using only a MediaDiff object.
 * If both ends of the Call are SDK clients, then "stop MoH" can only be done
 *    when the Call is on hold (by definition), and "remove media" can only be
 *    done when the Call is not on hold (requirement set by the API), but we
 *    can't make that assumption.
 *
 * In terms of SDP changes, it is defined as:
 *    1) No media was added.
 *    2) No media [sections] were removed.
 *    3) Some existing media stopped sending (ie. tracks were removed).
 * @method wasRemoveMedia
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasRemoveMedia (mediaDiff) {
  const { added, removed, changed } = mediaDiff

  /*
   * 1 & 2) No media sections were added or removed.
   */
  const wasntAdded = added.length === 0
  const wasntRemoved = removed.length === 0

  /*
   * 3a) Some media sections changed.
   */
  const wasChanged = changed.length > 0

  /*
   * 3b) For all media sections that changed,
   *      the only change was to stop sending.
   */
  const onlyStopSending = changed.every(({ media, changes }) => {
    return (
      changes.sending === MEDIA_TRANSITIONS.STOP &&
      // Receiving may be SAME or START, depending if the previous media
      //    direction was sendrecv (SAME) or sendonly (START).
      // TODO: Update `ChangedMedia` to include the pre-change media summary?
      changes.receiving !== MEDIA_TRANSITIONS.STOP
    )
  })

  /*
   * Unchanged media is not checked for.
   *    By definition, it is the same as before, and could in any state.
   */

  return wasntAdded && wasntRemoved && wasChanged && onlyStopSending
}

/**
 * A "start Music on Hold" operation is when media is currently held, and one
 *    audio media is changed to "send only".
 *
 * This checker does not guarantee that it was a "start MoH" scenario, and not
 *    a very specific "add media" scenario. It is an edge-case, but it is
 *    possible that both sides of the Call removed all media, then one side
 *    adds a single audio track. There is no way to tell the difference with
 *    only a MediaDiff object.
 *
 * In terms of SDP changes, it is defined as:
 *    0) A "add media" scenario where:
 *    1) One inactive audio section starts sending.
 *    2) All other media remains unchanged.
 *    3) Any unchanged media is inactive.
 * @method wasStartMoh
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasStartMoh (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  // 0) "Start MoH" is a "add media" scenario that has extra requirements.
  if (!wasAddMedia(mediaDiff)) {
    return false
  }

  /*
   * 1) Only one audio line should be changed, to send only.
   */
  const onlyAudioChange =
    // Only one audio line changed.
    changed.length === 1 &&
    changed[0].media.type === 'audio' &&
    // The change was to start sending media.
    changed[0].changes.sending === MEDIA_TRANSITIONS.START &&
    // Receiving media remains unchanged; not receiving.
    changed[0].changes.receiving === MEDIA_TRANSITIONS.SAME &&
    !changed[0].media.willReceive

  /*
   * 2) No media was added or removed.
   */
  const sameMedia = added.length === 0 && removed.length === 0

  /*
   * 3) For all media that is unchanged,
   *    they are not sending/receiving.
   */
  const inactiveUnchanged = unchanged.every(media => {
    return !media.willSend && !media.willReceive
  })

  return sameMedia && inactiveUnchanged && onlyAudioChange
}

/**
 * A "stop Music on Hold" operation is when media starts in a MoH state, then
 *    reverts back to an "on hold" state.
 *
 * This checker does not guarantee that it was a "stop MoH" scenario, and not
 *    a very specific "remove media" scenario. It is possible that a Call is
 *    established with one-way audio (and no video), then the audio is removed.
 *    There is no way to tell the difference with only a MediaDiff object.
 *
 * In terms of SDP changes, it is defined as:
 *    0) A "remove media" scenario where:
 *    1) One audio section changed stopped sending.
 *    2) All other media remains unchanged.
 *    3) Any unchanged media is inactive.
 * @method wasStopMoh
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasStopMoh (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  // 0) "Stop MoH" is a "remove media" scenario that has extra requirements.
  if (!wasRemoveMedia(mediaDiff)) {
    return false
  }

  /*
   * 1) Only one audio line should be changed, from send only to inactive.
   */
  const stopSendAudio =
    // Only one audio line changed.
    changed.length === 1 &&
    changed[0].media.type === 'audio' &&
    // The change was to stop sending media.
    changed[0].changes.sending === MEDIA_TRANSITIONS.STOP &&
    // Receiving media remains unchanged; not receiving.
    changed[0].changes.receiving === MEDIA_TRANSITIONS.SAME &&
    !changed[0].media.willReceive

  /*
   * 2) No media was added or removed.
   */
  const sameMedia = added.length === 0 && removed.length === 0

  /*
   * 3) For all media that is unchanged,
   *    they are not sending/receiving.
   */
  const inactiveUnchanged = unchanged.every(media => {
    return !media.willSend && !media.willReceive
  })

  return sameMedia && inactiveUnchanged && stopSendAudio
}

/**
 * A "no change" media operation is when all media sections remain the same.
 *    The remote operation was something other than changing media.
 *
 * In terms of SDP comparison, it is defined as:
 *    1) No media sections were added, removed, or changed.
 *    2) Some media remained unchanged.
 * @method wasNoChange
 * @param  {MediaDiff} mediaDiff Media differences described between two SDPs.
 * @return {boolean}
 */
export function wasNoChange (mediaDiff) {
  const { added, removed, changed, unchanged } = mediaDiff

  /*
   * No media did anything other than `unchanged`.
   */
  const noChange = added.length === 0 && removed.length === 0 && changed.length === 0

  /*
   * ...but there is media in the SDP.
   */
  const hasUnchanged = unchanged.length > 0

  return noChange && hasUnchanged
}

/**
 * Determines whether a MediaDiff object describes the SDP as having had media
 *    flowing (a media both sending and receiving; flowing in both directions)
 *    _before_ the SDP changes.
 * @method hadMediaFlowing
 * @param  {MediaDiff} mediaDiff
 * @return {boolean}   Whether the MediaDiff has media flowing before the change.
 */
export function hadMediaFlowing (mediaDiff) {
  const { removed, changed, unchanged } = mediaDiff

  /*
   * If any unchanged media is sending and receiving, then media was (and still
   *    is) flowing before the SDP change.
   */
  const unchangedFlowing = unchanged.some(media => {
    return media.willSend && media.willReceive
  })

  /*
   * If any changed media was sending and receiving, then media was flowing
   *    before the SDP change.
   */
  const changedFlowing = changed.some(({ media, changes }) => {
    /*
     * A changed media was sending before the change if
     *    1) the change was to stop sending, or
     *    2) sending didn't change, and is still sending.
     */
    const wasSending =
      changes.sending === MEDIA_TRANSITIONS.STOP || (changes.sending === MEDIA_TRANSITIONS.SAME && media.willSend)

    /*
     * A changed media was receiving before the change if
     *    1) the change was to stop receiving, or
     *    2) receiving didn't change, and is still receiving.
     */
    const wasReceiving =
      changes.receiving === MEDIA_TRANSITIONS.STOP ||
      (changes.receiving === MEDIA_TRANSITIONS.SAME && media.willReceive)

    return wasSending && wasReceiving
  })

  /*
   * If any removed media was sending and receiving, then media was flowing
   *    before the SDP change.
   */
  const removedFlowing = removed.some(media => {
    return media.willSend && media.willReceive
  })

  /*
   * Ignore `added` media, since that tells us nothing about media before the
   *    SDP change.
   */

  return unchangedFlowing || changedFlowing || removedFlowing
}

/**
 * Determines whether a MediaDiff object describes the SDP as having media
 *    flowing (a media both sending and receiving; flowing in both directions)
 *    _after_ the SDP changes.
 * @method hasMediaFlowing
 * @param  {MediaDiff} mediaDiff
 * @return {boolean}   Whether the MediaDiff has media flowing after the change.
 */
export function hasMediaFlowing (mediaDiff) {
  const { added, changed, unchanged } = mediaDiff

  /*
   * If any unchanged media is sending and receiving, then media is (and was)
   *    flowing after the SDP change.
   */
  const unchangedFlowing = unchanged.some(media => {
    return media.willSend && media.willReceive
  })

  /*
   * If any changed media is sending and receiving, then media is flowing
   *    after the SDP change.
   */
  const changedFlowing = changed.some(({ media, changes }) => {
    return media.willSend && media.willReceive
  })

  /*
   * If any added media is sending and receiving, then media is flowing
   *    after the SDP change.
   */
  const addedFlowing = added.some(media => {
    return media.willSend && media.willReceive
  })

  /*
   * Ignore `removed` media, since that tells us nothing about media after the
   *    SDP change.
   */

  return unchangedFlowing || changedFlowing || addedFlowing
}
